import React, { useCallback, useState } from 'react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { AlertContainer, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import {
  UploadIcon,
  FolderOpenIcon,
  XIcon,
  CheckIcon,
} from '@phosphor-icons/react';
import FileIcon from './FileIcon';
import { MAX_FILE_SIZE, UploadProgress } from 'src/types/UserDocuments';
import posthog from 'posthog-js';

interface FileUploadDropzoneProps {
  onFilesSelected: (files: File[]) => void;
  uploadProgress: UploadProgress[];
  isUploading: boolean;
  disabled?: boolean;
  maxFiles?: number;
  entityType?: string;
  entityId?: string;
  onRetryUpload?: (documentId: string, fileName: string) => void;
  onCancelUpload?: (documentId: string) => void;
  fillAvailableHeight?: boolean;
  minHeight?: string | number;
}

interface FileValidationError {
  file: File;
  error: string;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const validateFile = (file: File): string | null => {
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return `File size exceeds ${formatFileSize(MAX_FILE_SIZE)} limit`;
  }

  return null;
};

const FileUploadDropzone: React.FC<FileUploadDropzoneProps> = ({
  onFilesSelected,
  uploadProgress,
  isUploading,
  disabled = false,
  maxFiles = 10,
  onRetryUpload,
  onCancelUpload,
  fillAvailableHeight = false,
  minHeight,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    FileValidationError[]
  >([]);

  const handleFileValidation = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files);
      const errors: FileValidationError[] = [];
      const validFiles: File[] = [];

      // Check total file count
      if (fileArray.length > maxFiles) {
        setValidationErrors([
          {
            file: fileArray[0],
            error: `Maximum ${maxFiles} files allowed`,
          },
        ]);
        return;
      }

      // Validate each file
      fileArray.forEach((file) => {
        const error = validateFile(file);
        if (error) {
          errors.push({ file, error });
        } else {
          validFiles.push(file);
        }
      });

      setValidationErrors(errors);

      if (validFiles.length > 0) {
        onFilesSelected(validFiles);
      }
    },
    [maxFiles, onFilesSelected],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      if (disabled || isUploading) return;

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        handleFileValidation(files);
      }
    },
    [disabled, isUploading, handleFileValidation],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && !isUploading) {
        setIsDragOver(true);
      }
    },
    [disabled, isUploading],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFileValidation(files);
      }
      // Reset input value to allow selecting the same file again
      e.target.value = '';
    },
    [handleFileValidation],
  );

  const handleBrowseClick = () => {
    posthog.capture('file_upload_dropzone_browse_button_clicked', {
      context: 'file_upload_dropzone',
    });
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = '*/*';
    input.onchange = (e) => {
      handleFileInputChange(
        e as unknown as React.ChangeEvent<HTMLInputElement>,
      );
    };
    input.click();
  };

  return (
    <div>
      {/* Upload Drop Zone */}
      <Card
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          border-2 border-dashed transition-all duration-200 ease-in-out
          ${
            isDragOver
              ? 'border-blue-600 bg-blue-50'
              : disabled
                ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                : 'border-gray-400 bg-gray-50 cursor-pointer hover:bg-gray-100 hover:border-blue-600'
          }
          ${fillAvailableHeight ? 'flex flex-col flex-1 min-h-[200px]' : ''}
        `}
        style={minHeight && !fillAvailableHeight ? { minHeight } : undefined}
      >
        <CardContent
          className={`
            text-center py-12
            ${
              fillAvailableHeight
                ? 'flex-1 flex flex-col justify-center items-center'
                : ''
            }
          `}
        >
          {fillAvailableHeight ? (
            <div className="text-center">
              <h2
                className={`text-xl font-semibold mb-1 ${
                  disabled ? 'text-gray-600' : 'text-gray-900'
                }`}
              >
                {isDragOver ? 'Drop files here' : 'Drag & drop files here'}
              </h2>

              <p className="text-sm text-gray-600 mb-3">
                or click to browse files
              </p>

              <Button
                onClick={handleBrowseClick}
                disabled={disabled || isUploading}
                id="file-upload-dropzone-browse-button"
                className="mb-2"
              >
                <FolderOpenIcon className="w-4 h-4 mr-2" />
                Browse Files
              </Button>

              <div className="mt-2">
                <p className="text-xs text-gray-500">
                  Maximum {maxFiles} file{maxFiles !== 1 ? 's' : ''} | Maximum{' '}
                  file size: {formatFileSize(MAX_FILE_SIZE)}
                </p>
              </div>
            </div>
          ) : (
            <>
              <h2
                className={`text-xl font-semibold mb-1 ${
                  disabled ? 'text-gray-600' : 'text-gray-900'
                }`}
              >
                {isDragOver ? 'Drop files here' : 'Drag & drop files here'}
              </h2>

              <p className="text-sm text-gray-600 mb-3">
                or click to browse files
              </p>

              <Button
                onClick={handleBrowseClick}
                disabled={disabled || isUploading}
                id="file-upload-dropzone-browse-button"
                className="mb-2"
              >
                <FolderOpenIcon className="w-4 h-4 mr-2" />
                Browse Files
              </Button>

              <div className="mt-2">
                <p className="text-xs text-gray-500">
                  Maximum {maxFiles} file{maxFiles !== 1 ? 's' : ''} | Maximum{' '}
                  file size: {formatFileSize(MAX_FILE_SIZE)}
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="mt-2">
          {validationErrors.map((error, index) => (
            <AlertContainer key={index} className="mb-2">
              <AlertDescription>
                <strong>{error.file.name}:</strong> {error.error}
              </AlertDescription>
              <Button
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2 h-6 w-6 p-0"
                onClick={() => {
                  setValidationErrors((prev) =>
                    prev.filter((_, i) => i !== index),
                  );
                }}
              >
                <XIcon className="h-4 w-4" />
              </Button>
            </AlertContainer>
          ))}
        </div>
      )}

      {/* Upload Progress */}
      {uploadProgress.length > 0 && (
        <div className="mt-3">
          <h3 className="text-lg font-semibold mb-2">Upload Progress</h3>

          {uploadProgress.map((progress) => (
            <Card key={progress.documentId} className="mb-4">
              <CardContent className="pb-4">
                <div className="flex items-center mb-1">
                  <FileIcon
                    fileName={progress.fileName}
                    size="small"
                    showBackground={false}
                  />
                  <div className="ml-1 flex-grow">
                    <p className="text-sm font-medium">{progress.fileName}</p>
                  </div>

                  {progress.status === 'uploaded' && onCancelUpload && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => onCancelUpload(progress.documentId)}
                    >
                      <XIcon className="h-4 w-4" />
                    </Button>
                  )}

                  {progress.status === 'failed' && onRetryUpload && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-blue-600 hover:text-blue-700"
                      title="Retry upload"
                      onClick={() =>
                        onRetryUpload(progress.documentId, progress.fileName)
                      }
                    >
                      <UploadIcon className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                {progress.status === 'uploading' && (
                  <Progress value={progress.progress} className="h-1.5" />
                )}

                {progress.status === 'uploaded' && (
                  <div className="flex items-center">
                    <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center mr-2">
                      <CheckIcon className="w-2 h-2 text-white" />
                    </div>
                    <p className="text-xs text-green-600 ml-0.5">
                      Upload completed
                    </p>
                  </div>
                )}

                {progress.status === 'failed' && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center mr-2">
                        <XIcon className="w-2 h-2 text-white" />
                      </div>
                      <p className="text-xs text-red-600 ml-0.5">
                        {progress.error || 'Upload failed'}
                      </p>
                    </div>
                    {onRetryUpload && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700"
                        onClick={() =>
                          onRetryUpload(progress.documentId, progress.fileName)
                        }
                      >
                        <UploadIcon className="w-4 h-4 mr-1" />
                        Retry
                      </Button>
                    )}
                  </div>
                )}

                {progress.status === 'uploading' && (
                  <div className="flex justify-between mt-1">
                    <p className="text-xs text-gray-600">
                      {Math.round(progress.progress)}% uploaded
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUploadDropzone;
